import React, { useState, useEffect } from 'react';
import { useRole } from '../hooks/useRole';
import {
  lineTotal as computeLineTotal, lineCost, resolveUnitCost, isUnitCostKnown, lineMargin, lineMarginPct,
} from '../utils/lineMath';

// Light blue palette (matches the redesigned pages and ProductPickerModal)
const C_TEXT = '#03045E';
const C_TEXT_2 = '#6B7C93';
const C_TEXT_3 = '#6B7C93';
const C_BORDER = '#E3ECF5';
const PRIMARY = '#0077B6';

const labelStyle = {
  color: C_TEXT_2,
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: 1,
  textTransform: 'uppercase',
  marginBottom: 6,
  display: 'block',
};
const inputStyle = {
  width: '100%',
  padding: '10px 14px',
  background: '#FFFFFF',
  border: `1px solid ${C_BORDER}`,
  borderRadius: 10,
  color: C_TEXT,
  fontSize: 14,
  outline: 'none',
  transition: 'border-color 0.3s, box-shadow 0.3s',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
};
const gradientButton = {
  background: PRIMARY,
  border: 'none',
  color: '#fff',
  padding: '12px 24px',
  borderRadius: 12,
  fontSize: 14,
  fontWeight: 700,
  cursor: 'pointer',
  letterSpacing: 0.5,
  boxShadow: '0 4px 14px rgba(0, 119, 182, 0.3)',
  transition: 'transform 0.3s, box-shadow 0.3s',
};
const darkButton = {
  background: '#FFFFFF',
  border: '1px solid #E3ECF5',
  color: C_TEXT,
  padding: '12px 24px',
  borderRadius: 12,
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'background 0.3s',
};

export const LEVEL_COLORS = {
  A: { bg: '#E6F1FB', border: '#0077B6', text: '#0077B6' },
  B: { bg: '#E6F7EE', border: '#0F6E56', text: '#0F6E56' },
  C: { bg: '#FDF1DD', border: '#854F0B', text: '#854F0B' },
};

const getPriceForLevel = (product, level) => {
  if (!product) return 0;
  if (level === 'A') return parseFloat(product.priceA) || 0;
  if (level === 'B') return parseFloat(product.priceB) || 0;
  if (level === 'C') return parseFloat(product.priceC) || 0;
  return parseFloat(product.priceA) || 0;
};

function LineItemEditorModal({ open, item, onClose, onSave, products, globalPriceLevel }) {
  // Cost and margin are admin-only. Read here rather than passed in, so the
  // gate lives beside the thing it guards and neither page has to remember it.
  const { isAdmin } = useRole();
  const [draft, setDraft] = useState(item);

  // Sync draft whenever the modal opens with a new item (deep copy so unsaved edits don't leak)
  useEffect(() => {
    if (open && item) setDraft({ ...item });
  }, [open, item]);

  if (!open || !draft) return null;

  // Try to resolve the linked product (by productId first, then by SKU)
  const linkedProduct = draft.productId
    ? products.find(p => p.id === draft.productId)
    : (draft.sku && !['—', 'NON-STOCK', 'TEMPLATE'].includes(draft.sku)
        ? products.find(p => p.sku === draft.sku && p.isActive !== false)
        : null);

  const lineTotalValue = computeLineTotal(draft);

  // ── Cost and margin, derived every render ──
  // Through the SAME helpers the document totals use, so the figures here and
  // the Office Use totals can never disagree, and nothing is rounded by a rule
  // of its own. The line total is not recomputed — computeLineTotal above is
  // the app's one definition of it.
  const unitCost = resolveUnitCost(draft, products);
  const costKnown = isUnitCostKnown(unitCost);
  const lineCostValue = costKnown ? lineCost(draft, unitCost) : 0;
  const marginValue = costKnown ? lineMargin(draft, unitCost) : 0;
  const marginPct = costKnown ? lineMarginPct(draft, unitCost) : null;

  const handleResetToGlobal = () => {
    setDraft(d => ({
      ...d,
      unitPrice: linkedProduct ? getPriceForLevel(linkedProduct, globalPriceLevel) : d.unitPrice,
      itemPriceLevel: null,
      manualPrice: false,
    }));
  };

  const handleApply = () => {
    onSave(draft);
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 60,
        background: 'rgba(3, 4, 94, 0.35)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#FFFFFF',
          border: `1px solid ${C_BORDER}`,
          borderRadius: 20, padding: 28,
          width: '100%', maxWidth: 480,
          maxHeight: '92vh', overflowY: 'auto',
          boxShadow: '0 20px 50px rgba(3, 4, 94, 0.18)',
          position: 'relative',
          color: C_TEXT,
        }}
        onClick={e => e.stopPropagation()}
      >
        <style>{`
          select.lim-dark-select option { background: #FFFFFF; color: #03045E; }
        `}</style>

        <div style={{
          height: 2,
          background: PRIMARY,
          marginBottom: 20, marginLeft: -28, marginRight: -28, marginTop: -28,
        }} />

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ color: C_TEXT, fontSize: 18, fontWeight: 800, margin: 0 }}>✏️ Edit Line Item</h3>
          <button
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', color: C_TEXT_3, fontSize: 22, lineHeight: 1, cursor: 'pointer' }}
          >✕</button>
        </div>

        {/* SKU + source badges */}
        {(draft.sku && draft.sku !== '—') || draft.isNonStockCustom ? (
          <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {draft.sku && draft.sku !== '—' && (
              <span style={{
                fontFamily: 'monospace', color: '#0077B6', fontSize: 13, fontWeight: 700,
                background: '#E6F1FB', padding: '4px 12px', borderRadius: 8,
                border: '1px solid #E3ECF5',
              }}>
                SKU: {draft.sku}
              </span>
            )}
            {draft.isNonStockCustom && (
              <span style={{ fontSize: 11, color: '#854F0B', fontWeight: 700 }}>NON-STOCK</span>
            )}
            {draft.fromEstimator && (
              <span style={{ fontSize: 11, color: '#0077B6' }}>🔧 From Estimator</span>
            )}
            {draft.fromTemplate && (
              <span style={{ fontSize: 11, color: '#0096C7' }}>📋 From Template</span>
            )}
            {draft.fromStockBundle && (
              <span style={{ fontSize: 11, color: '#0077B6' }}>📦 From Bundle</span>
            )}
          </div>
        ) : null}

        {/* Description */}
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Description</label>
          <textarea
            value={draft.description || ''}
            onChange={e => setDraft({ ...draft, description: e.target.value })}
            rows={3}
            style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }}
            onFocus={e => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
            onBlur={e => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
          />
        </div>

        {/* Qty + Unit */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
          <div>
            <label style={labelStyle}>Quantity</label>
            <input
              type="number"
              value={draft.qty}
              onChange={e => setDraft({ ...draft, qty: e.target.value })}
              onFocus={e => e.target.select()}
              style={inputStyle}
              min="0" step="0.01"
            />
          </div>
          <div>
            <label style={labelStyle}>Unit</label>
            <select
              value={draft.unit || 'pc'}
              onChange={e => setDraft({ ...draft, unit: e.target.value })}
              className="lim-dark-select"
              style={{ ...inputStyle, cursor: 'pointer' }}
              onFocus={e => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
              onBlur={e => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
            >
              {['pc', 'ft', 'ea', 'roll', 'lot', 'bag', 'lb', 'gal'].map(u => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Per-item Price Level — only if linked to a product */}
        {linkedProduct && (
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Price Level (for this item only)</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {['A', 'B', 'C'].map(level => {
                const levelPrice = getPriceForLevel(linkedProduct, level);
                const isActive = draft.itemPriceLevel === level && !draft.manualPrice;
                const c = LEVEL_COLORS[level];
                return (
                  <button
                    key={level}
                    onClick={() => setDraft({
                      ...draft,
                      unitPrice: levelPrice,
                      itemPriceLevel: level,
                      manualPrice: false,
                    })}
                    style={{
                      flex: 1, padding: '10px 8px',
                      background: isActive ? c.bg : '#FFFFFF',
                      border: `1px solid ${isActive ? c.border : '#E3ECF5'}`,
                      borderRadius: 10,
                      color: isActive ? c.text : C_TEXT_3,
                      cursor: 'pointer',
                      transition: 'background 0.3s, border-color 0.3s, color 0.3s',
                      textAlign: 'center',
                    }}
                  >
                    <span style={{ display: 'block', fontSize: 14, fontWeight: 700 }}>{level}</span>
                    <span style={{ display: 'block', fontSize: 12, marginTop: 2 }}>
                      ${levelPrice.toFixed(2)}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Cost — non-stock items only (internal, per-unit) */}
        {draft.isNonStockCustom && (
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Cost</label>
            <input
              type="number"
              step="0.01"
              value={draft.cost ?? ''}
              onChange={e => setDraft({ ...draft, cost: e.target.value })}
              onFocus={e => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
              onBlur={e => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
              placeholder="0.00"
              style={inputStyle}
            />
          </div>
        )}

        {/* Unit Price (manual input) */}
        <div style={{ marginBottom: 8 }}>
          <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span>Unit Price</span>
            {draft.manualPrice && (
              <span style={{ color: '#854F0B', textTransform: 'none', letterSpacing: 0 }}>✏️ Manual</span>
            )}
            {draft.itemPriceLevel && !draft.manualPrice && (
              <span style={{ color: LEVEL_COLORS[draft.itemPriceLevel].text, textTransform: 'none', letterSpacing: 0 }}>
                [Level {draft.itemPriceLevel}]
              </span>
            )}
          </label>
          <input
            type="number"
            step="0.01"
            value={draft.unitPrice}
            onChange={e => setDraft({
              ...draft,
              unitPrice: e.target.value,
              manualPrice: true,
              itemPriceLevel: null,
            })}
            onFocus={e => e.target.select()}
            style={{ ...inputStyle, color: '#0077B6', fontWeight: 700, fontSize: 16 }}
          />
        </div>

        {(draft.itemPriceLevel || draft.manualPrice) && (
          <button
            onClick={handleResetToGlobal}
            style={{
              background: 'transparent', border: 'none',
              color: C_TEXT_3, fontSize: 12,
              cursor: 'pointer', textDecoration: 'underline',
              padding: 0, marginBottom: 12,
            }}
            onMouseEnter={e => e.currentTarget.style.color = C_TEXT_2}
            onMouseLeave={e => e.currentTarget.style.color = C_TEXT_3}
          >
            ↺ Reset to follow document price level ({globalPriceLevel})
          </button>
        )}

        <p style={{ color: C_TEXT_3, fontSize: 11, marginTop: 0, marginBottom: 16 }}>
          Type a custom price, or use the level buttons above to apply standard pricing.
        </p>

        {/* Return Credit — flips this line negative (credit back to customer) */}
        <label style={{
          display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16,
          cursor: 'pointer',
          background: draft.isReturnCredit ? '#FBECEC' : '#CDE7F5',
          border: `1px solid ${draft.isReturnCredit ? '#E9C3C3' : '#E3ECF5'}`,
          borderRadius: 12, padding: '12px 14px',
        }}>
          <input
            type="checkbox"
            checked={!!draft.isReturnCredit}
            onChange={e => setDraft({ ...draft, isReturnCredit: e.target.checked })}
            style={{ accentColor: '#A32D2D', width: 16, height: 16 }}
          />
          <span style={{ fontSize: 13, fontWeight: 700, color: draft.isReturnCredit ? '#A32D2D' : C_TEXT }}>
            ↩ Return Credit to Customer
          </span>
          <span style={{ fontSize: 11, color: C_TEXT_3, marginLeft: 'auto' }}>subtracts from totals</span>
        </label>

        {/* Line Total */}
        <div style={{
          background: '#CDE7F5',
          border: '1px solid #E3ECF5',
          borderRadius: 12, padding: '14px 16px',
          marginBottom: 20,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span style={{ color: C_TEXT_2, fontSize: 13, fontWeight: 600 }}>
            {draft.isReturnCredit ? 'Line Credit' : 'Line Total'}
          </span>
          <span style={{
            fontSize: 22, fontWeight: 800,
            color: draft.isReturnCredit ? '#A32D2D' : '#0077B6',
          }}>
            {draft.isReturnCredit ? '-' : ''}${Math.abs(lineTotalValue).toFixed(2)}
          </span>
        </div>

        {/* ── COST AND MARGIN — ADMIN ONLY, AND READ-ONLY ──────────────────
            Internal figures. Four numbers, no inputs, nothing writable, and
            nothing here is ever stored: every value is derived from the draft
            and the price list on each render, so it follows the price and
            quantity as they are typed.

            isAdmin is checked HERE rather than left to routing: the shop role
            never reaches a document editor, but a screen that shows cost
            should not depend on a redirect elsewhere to stay internal.

            ⚠️ THIS MUST NEVER REACH PAPER. The modal is mounted only by the two
            document editors; the printed document, the packing slip and the
            shop screen are rendered by ViewDocument and Shop, which never
            import it and hold no cost field of any kind. */}
        {isAdmin && (
          <div style={{
            background: '#FFFFFF', border: `1px solid ${C_BORDER}`,
            borderRadius: 12, padding: '12px 16px', marginBottom: 20,
          }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: costKnown ? 8 : 0 }}>
              <span style={{ ...labelStyle, marginBottom: 0 }}>Internal — not printed</span>
              {!costKnown && (
                <span style={{ color: '#854F0B', fontSize: 12, fontWeight: 700 }}>Cost not available</span>
              )}
            </div>
            {costKnown ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px', fontSize: 13 }}>
                <span style={{ color: C_TEXT_2 }}>Unit cost</span>
                <span style={{ color: C_TEXT, fontWeight: 700, textAlign: 'right' }}>${unitCost.toFixed(2)}</span>

                <span style={{ color: C_TEXT_2 }}>Line cost</span>
                <span style={{ color: C_TEXT, fontWeight: 700, textAlign: 'right' }}>
                  {draft.isReturnCredit ? '-' : ''}${Math.abs(lineCostValue).toFixed(2)}
                </span>

                {/* A RETURN CREDIT's margin is NEGATIVE — margin handed back to
                    the customer — so it is labelled as that and shown in the
                    credit's own red. No percentage there: a positive percent
                    beside a negative figure invites misreading. */}
                <span style={{ color: C_TEXT_2 }}>
                  {draft.isReturnCredit ? 'Margin given back' : 'Margin'}
                </span>
                <span style={{
                  fontWeight: 700, textAlign: 'right',
                  color: marginValue >= 0 ? '#0F6E56' : '#A32D2D',
                }}>
                  {marginValue < 0 ? '-' : ''}${Math.abs(marginValue).toFixed(2)}
                </span>

                {!draft.isReturnCredit && marginPct !== null && (
                  <>
                    <span style={{ color: C_TEXT_2 }}>Margin %</span>
                    <span style={{ fontWeight: 700, textAlign: 'right', color: marginPct >= 0 ? '#0F6E56' : '#A32D2D' }}>
                      {marginPct.toFixed(1)}%
                    </span>
                  </>
                )}
              </div>
            ) : (
              <p style={{ color: C_TEXT_2, fontSize: 12, margin: '6px 0 0 0' }}>
                No cost on this line and none in the price list, so no margin is shown —
                a margin computed from a missing cost would read as profit this line may not have.
              </p>
            )}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={handleApply}
            style={{ ...gradientButton, flex: 1 }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 119, 182, 0.45)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 14px rgba(0, 119, 182, 0.3)';
            }}
          >
            💾 Apply Changes
          </button>
          <button
            onClick={onClose}
            style={{ ...darkButton, flex: 1 }}
            onMouseEnter={e => e.currentTarget.style.background = '#CDE7F5'}
            onMouseLeave={e => e.currentTarget.style.background = '#FFFFFF'}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default LineItemEditorModal;
